import { useTranslation } from 'react-i18next';
import { Pause, Play, Square, Keyboard, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CallSessionView } from '@/hooks/useCallSessionView';
import { formatClock } from './time';

interface Props {
  view: CallSessionView;
  now: number;
  blocked: 'reconciling' | 'reconcileFailed' | 'narrow' | null;
  onStart(): void;
  onPause(): void;
  onResume(): void;
  onStop(): void;
  onHelp(): void;
  onRetryReconcile(): void;
}

/** Spec §4.1: the session at a glance, and the session commands, far from « Raccrocher » (D5). */
export function SessionBar({ view, now, blocked, onStart, onPause, onResume, onStop, onHelp, onRetryReconcile }: Props) {
  const { t } = useTranslation(['dialer']);
  const { phase, stats } = view;
  const live = phase !== 'prep' && phase !== 'finished';
  const elapsed = view.sessionStartedAt ? formatClock(now - view.sessionStartedAt) : null;
  const deviceIssue = view.device !== 'ready';
  const dot = phase === 'paused' ? 'var(--vf-status-callback-fg)' : live ? 'var(--vf-status-connected-fg)' : 'var(--vf-text-3)';

  return (
    <div className="border-b" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
      <h1 className="sr-only">{t('dialer:callPage.title')}</h1>
      <div className="flex min-h-14 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--vf-text-1)' }}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: dot }} aria-hidden />
          {t(`dialer:callPage.session.${phase}`)}
        </div>
        <div className="text-xs tabular-nums" style={{ color: 'var(--vf-text-2)' }}>
          {t('dialer:callPage.session.lines', { count: view.lines.filter((l) => ['dialing', 'ringing', 'connected'].includes(l.state)).length })}
          {' · '}
          {t('dialer:callPage.session.remaining', { count: view.remaining })}
        </div>
        <div className="text-xs tabular-nums" style={{ color: 'var(--vf-text-2)' }}>
          {t('dialer:callPage.session.stats', { calls: stats.calls, connected: stats.connected, meetings: stats.meetings })}
          {elapsed ? ` · ${elapsed}` : ''}
        </div>
        <div className="flex-1" />
        {phase === 'prep' && (
          <Button size="sm" className="min-h-11" onClick={onStart} disabled={!!blocked || deviceIssue} aria-keyshortcuts="Control+Enter Meta+Enter">
            {blocked === 'reconciling' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <Play className="mr-1.5 h-4 w-4" aria-hidden />}
            {t('dialer:callPage.session.start')}
          </Button>
        )}
        {(phase === 'dialing' || phase === 'connected') && (
          <Button size="sm" variant="outline" className="min-h-11" onClick={onPause} aria-keyshortcuts="Control+Enter Meta+Enter">
            <Pause className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.session.pause')}
          </Button>
        )}
        {phase === 'paused' && (
          <Button size="sm" className="min-h-11" onClick={onResume} disabled={!!blocked || deviceIssue} aria-keyshortcuts="Control+Enter Meta+Enter">
            <Play className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.session.resume')}
          </Button>
        )}
        {live && (
          <Button size="sm" variant="ghost" className="min-h-11" onClick={onStop}>
            <Square className="mr-1.5 h-4 w-4" aria-hidden />{t('dialer:callPage.session.stop')}
          </Button>
        )}
        <Button size="icon" variant="ghost" className="min-h-11 min-w-11" onClick={onHelp} aria-label={t('dialer:callPage.session.help')} aria-keyshortcuts="?">
          <Keyboard className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {(deviceIssue || blocked) && (
        <div
          role="status"
          className="flex items-center gap-2 px-4 py-1.5 text-xs"
          style={{ background: 'var(--vf-yellow-bg)', color: 'var(--vf-text-1)' }}
        >
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          {/* One banner slot (DES-14): telephony first, then the reconciliation. */}
          {deviceIssue
            // i18n-exempt: error codes shown as codes, not text
            ? <>{t(`dialer:callPage.device.${view.device}`)} <code className="font-mono text-[11px]">{view.device === 'mic_denied' ? 'MIC_DENIED' : 'TEL_OFFLINE'}</code></>
            : <>
                {t(`dialer:callPage.session.${blocked}`)}
                {blocked === 'reconcileFailed' && (
                  <>
                    {/* i18n-exempt: an error code shown as a code, not text */}
                    <code className="font-mono text-[11px]">CALL_STATE_UNKNOWN</code>
                    <Button size="sm" variant="outline" className="ml-2 min-h-11" onClick={onRetryReconcile}>{t('dialer:callPage.errors.retry')}</Button>
                  </>
                )}
              </>}
        </div>
      )}
    </div>
  );
}
